import type { CustomerSalutation, CustomerType } from "@autoflow/types";
import { http } from "../../shared/api/http";
import type {
  CustomerAttachment, CustomerHistory, CustomerListResponse, CustomerRecord, CustomerTag
} from "./customer-types";

export type CustomerInput = {
  type: CustomerType;
  salutation?: CustomerSalutation;
  firstName?: string;
  lastName?: string;
  companyName?: string;
  email?: string;
  phone: string;
  alternatePhone?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  preferredLocale: "en" | "zh";
};

export const customersApi = {
  async list(search = "", page = 1) {
    return (await http.get<CustomerListResponse>("/v1/customers", { params: { search: search || undefined, page, pageSize: 20 } })).data;
  },
  async get(id: string) {
    return (await http.get<CustomerRecord>(`/v1/customers/${id}`)).data;
  },
  async create(input: CustomerInput) {
    return (await http.post<CustomerRecord>("/v1/customers", input)).data;
  },
  async update(id: string, input: Partial<CustomerInput>) {
    return (await http.patch<CustomerRecord>(`/v1/customers/${id}`, input)).data;
  },
  async remove(id: string) {
    await http.delete(`/v1/customers/${id}`);
  },
  async addNote(id: string, body: string) {
    return (await http.post(`/v1/customers/${id}/notes`, { body })).data;
  },
  async removeNote(id: string, noteId: string) {
    await http.delete(`/v1/customers/${id}/notes/${noteId}`);
  },
  async history(id: string) {
    return (await http.get<CustomerHistory[]>(`/v1/customers/${id}/history`)).data;
  },
  async tags() {
    return (await http.get<CustomerTag[]>("/v1/customers/tags")).data;
  },
  async assignTag(id: string, tagId: string) {
    return (await http.post(`/v1/customers/${id}/tags`, { tagId })).data;
  },
  async removeTag(id: string, tagId: string) {
    await http.delete(`/v1/customers/${id}/tags/${tagId}`);
  },
  async uploadAttachment(id: string, file: File) {
    const form = new FormData();
    form.append("file", file);
    return (await http.post<CustomerAttachment>(`/v1/customers/${id}/attachments`, form, {
      headers: { "Content-Type": "multipart/form-data" }
    })).data;
  },
  async removeAttachment(id: string, attachmentId: string) {
    await http.delete(`/v1/customers/${id}/attachments/${attachmentId}`);
  }
};
